import { useOne } from '@refinedev/core';
import { UploadedFile } from './types';
import { useFileUploadActions } from './useFileUploadActions';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';

interface UseFileUploadOptions {
  autoRefreshProcessing?: boolean;
}

interface UseFileUploadResult {
  file: UploadedFile | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  deleteFile: () => Promise<void>;
  setFileResolved: (isResolved: boolean) => Promise<void>;
  moveFile: (parentId: string | null) => Promise<void>;
  getFileStatus: () => { color: string; label: string; showLoader?: boolean };
  refetchFile: () => Promise<void>;
}

export const useFileUpload = (
  uploadedFileId: UploadedFile['id'] | undefined,
  { autoRefreshProcessing = false }: UseFileUploadOptions = {},
): UseFileUploadResult => {
  const { clientId } = useCurrentClient();
  const actions = useFileUploadActions();

  const resource = `clients/${clientId}/data-upload-files`;

  const { data, isLoading, isError, refetch } = useOne<UploadedFile>({
    resource,
    id: uploadedFileId,
    queryOptions: {
      enabled: !!uploadedFileId,
      refetchInterval: autoRefreshProcessing ? 5000 : false,
    },
  });

  const file = data?.data;

  const refetchFile = async () => {
    await refetch();
  };

  return {
    file,
    isLoading,
    error: isError ? new Error('Failed to load file') : actions.error,
    isError,
    deleteFile: () => {
      if (!uploadedFileId) {
        return Promise.reject(new Error('File ID is required'));
      }
      return actions.deleteFile(uploadedFileId);
    },
    setFileResolved: (isResolved: boolean) =>
      file
        ? actions.setFileResolved(file, isResolved)
        : Promise.reject(new Error('File not found')),
    moveFile: (parentId: string | null) => {
      if (!data?.data.file) {
        return Promise.reject(new Error('File ID is required'));
      }
      return actions.moveFile(data?.data, parentId);
    },
    getFileStatus: () =>
      file ? actions.getFileStatus(file) : { color: 'gray.6', label: 'Not Found' },
    refetchFile,
  };
};
